import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import '../styles/auth.css';

const Signup = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [role, setRole] = useState('Student');

  const { signup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/workspace';

  // Password strength checker
  const getPasswordStrength = (pass) => {
    if (!pass) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    if (pass.length >= 6) strength++;
    if (pass.length >= 10) strength++;
    if (/[a-z]/.test(pass) && /[A-Z]/.test(pass)) strength++;
    if (/\d/.test(pass)) strength++;
    if (/[^a-zA-Z0-9]/.test(pass)) strength++;

    if (strength <= 2) return { strength, label: 'Weak', color: '#ef4444' };
    if (strength <= 3) return { strength, label: 'Medium', color: '#f59e0b' };
    return { strength, label: 'Strong', color: '#10b981' };
  };

  const passwordStrength = getPasswordStrength(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (!name || !email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    if (name.length < 2) {
      setError('Name must be at least 2 characters');
      setLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    // Password validation
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (!acceptTerms) {
      setError('Please accept the terms and conditions');
      setLoading(false);
      return;
    }

    try {
      const result = await signup(name, email, password, role);
      
      if (result.success) {
        const target = from === '/' ? '/workspace' : from;
        navigate(target, { replace: true });
      } else {
        setError(result.error || 'Signup failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-background">
        <div className="auth-shape shape-1"></div>
        <div className="auth-shape shape-2"></div>
        <div className="auth-shape shape-3"></div>
      </div>

      <div className="auth-content">
        <div className="auth-card signup-card" style={{ padding: '2rem', maxWidth: '480px' }}>
          <div className="auth-header" style={{ marginBottom: '1.25rem' }}>
            <h1 className="auth-title" style={{ fontSize: '1.75rem', margin: 0 }}>Create Account</h1>
            <p className="auth-subtitle" style={{ fontSize: '0.875rem', marginTop: '0.25rem', color: '#94a3b8' }}>
              Join us to explore the ancient wisdom of Ayurveda
            </p>
          </div>

          {error && (
            <div className="auth-error" style={{ marginBottom: '1rem', padding: '0.75rem 1rem' }}>
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form">
            {/* Row 1: Full Name */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <input
                type="text"
                id="name"
                className="form-input"
                placeholder="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
                autoComplete="name"
              />
            </div>

            {/* Row 2: Role & Email (flex) */}
            <div className="form-row-flex" style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
              <div className="form-group" style={{ flex: '1 1 30%', marginBottom: 0 }}>
                <div className="select-input-wrapper" style={{ position: 'relative' }}>
                  <select
                    id="role"
                    className="form-input"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    disabled={loading}
                    style={{
                      appearance: 'none',
                      cursor: 'pointer',
                      paddingRight: '1.75rem'
                    }}
                  >
                    <option value="Student">Student</option>
                    <option value="Doctor">Doctor</option>
                  </select>
                  <div style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    color: '#9ca3af',
                    fontSize: '0.7rem'
                  }}>▼</div>
                </div>
              </div>
              <div className="form-group" style={{ flex: '1 1 70%', marginBottom: 0 }}>
                <input
                  type="email"
                  id="email"
                  className="form-input"
                  placeholder="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Row 3: Password & Confirm Password (flex) */}
            <div className="form-row-flex" style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
              <div className="form-group" style={{ flex: '1 1 50%', marginBottom: 0 }}>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    className="form-input"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="form-group" style={{ flex: '1 1 50%', marginBottom: 0 }}>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    className="form-input"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex="-1"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>

            {/* Password Feedback Info */}
            {(password || (confirmPassword && password === confirmPassword)) && (
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between', fontSize: '0.775rem', marginBottom: '0.875rem', marginTop: '-0.375rem', padding: '0 0.25rem' }}>
                {password && (
                  <span style={{ color: passwordStrength.color }}>
                    Strength: {passwordStrength.label}
                  </span>
                )}
                {confirmPassword && password === confirmPassword && (
                  <span style={{ color: '#10b981' }}>
                    ✓ Passwords match
                  </span>
                )}
              </div>
            )}

            {/* Row 4: Accept Terms Checkbox */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="checkbox-label terms-label" style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  style={{ marginRight: '0.5rem' }}
                />
                <span>
                  I agree to the{' '}
                  <Link to="/terms" target="_blank" className="inline-link">
                    Terms
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy-policy" target="_blank" className="inline-link">
                    Privacy Policy
                  </Link>
                </span>
              </label>
            </div>

            {/* Row 5: Submit Button */}
            <button
              type="submit"
              className="auth-button"
              disabled={loading}
              style={{ padding: '0.75rem 1.5rem', fontSize: '0.95rem' }}
            >
              {loading ? (
                <>
                  <div className="button-spinner"></div>
                  Creating account...
                </>
              ) : (
                "Create Account"
              )}
            </button>
          </form>

          <div className="auth-divider" style={{ margin: '1rem 0' }}>
            <span>or</span>
          </div>

          <div className="auth-footer" style={{ marginTop: '0.25rem' }}>
            <p style={{ fontSize: '0.85rem', margin: 0 }}>
              Already have an account?{' '}
              <Link to="/login" className="auth-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;

